import type {
  LabMarkerTVitalLabMarkerMetadataT as LabMarkerT,
  LabTestCollectionMethodT,
  LabTestTVitalLabTestMetadataTVitalLabMarkerMetadataT as LabTestT,
} from '@lotushealth/lotushealth_js_sdk/schemas';

import type { ICD10CodeT } from '@/entities/labs';

/**
 * Wizard step IDs for the lab order flow.
 *
 * - select-test: Browse / search existing lab tests (default entry point)
 * - custom-test: Build a custom test from individual biomarkers
 * - disease-codes: ICD-10 codes, AOE questions, notes, priority
 * - review: Review & submit the order
 * - confirmation: Success / failure outcome
 */
export type LabOrderWizardStepId =
  | 'select-test'
  | 'custom-test'
  | 'disease-codes'
  | 'review'
  | 'confirmation';

export type LabOrderDraftMode = 'existing_test' | 'custom_test';

export interface LabOrderDraftBase {
  mode: LabOrderDraftMode;
  notes: string;
  priority: boolean;
  aoeAnswersById: Record<string, string>;
  icdCodes: ICD10CodeT[];
}

export interface ExistingTestLabOrderDraft extends LabOrderDraftBase {
  mode: 'existing_test';
  labTestId: string;
  labTestName?: string;
  /** Cached lab test object for AOE rendering and review. */
  selectedLabTest?: LabTestT;
}

export interface CustomTestLabOrderDraft extends LabOrderDraftBase {
  mode: 'custom_test';
  name: string;
  description: string;
  method: LabTestCollectionMethodT;
  selectedMarkers: LabMarkerT[];
}

export type LabOrderDraft = ExistingTestLabOrderDraft | CustomTestLabOrderDraft;

/** Initial draft state for existing test mode. */
export const INITIAL_EXISTING_TEST_DRAFT: ExistingTestLabOrderDraft = {
  mode: 'existing_test',
  labTestId: '',
  labTestName: undefined,
  selectedLabTest: undefined,
  notes: '',
  priority: false,
  aoeAnswersById: {},
  icdCodes: [],
};

/** Initial draft state for custom test mode. */
export const INITIAL_CUSTOM_TEST_DRAFT: CustomTestLabOrderDraft = {
  mode: 'custom_test',
  name: '',
  description: '',
  method: 'walk_in_test',
  selectedMarkers: [],
  notes: '',
  priority: false,
  aoeAnswersById: {},
  icdCodes: [],
};
